import { useState, useEffect } from 'react';
import { vyDate } from '@vy/utils';

type UseEditableDateFieldProps = {
  value: Date | undefined;
  onSave: (date: Date | null) => Promise<void>;
};

export const useEditableDateField = ({ value, onSave }: UseEditableDateFieldProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedDate, setEditedDate] = useState<Date | null>(value ?? null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [shouldAutoOpenPicker, setShouldAutoOpenPicker] = useState(false);

  useEffect(() => {
    setEditedDate(value ?? null);
  }, [value]);

  useEffect(() => {
    if (isEditMode && shouldAutoOpenPicker) {
      setTimeout(() => {
        setIsDatePickerOpen(true);
        setShouldAutoOpenPicker(false);
      }, 0);
    }
  }, [isEditMode, shouldAutoOpenPicker]);

  const confirmSave = async () => {
    const isSameDate = vyDate(editedDate).isSame(vyDate(value));

    if (!isSameDate) {
      await onSave(editedDate);
    }
    setIsEditMode(false);
    setIsDatePickerOpen(false);
  };

  const cancelEdit = () => {
    setEditedDate(value ?? null);
    setIsEditMode(false);
    setIsDatePickerOpen(false);
  };

  const handleCalendarClick = () => {
    setIsDatePickerOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleConfirmDialogSubmit = () => {
    setIsConfirmDialogOpen(false);
    setIsEditMode(true);
    setShouldAutoOpenPicker(true);
  };

  const handleDatePickerChange = (newDate: Date | null) => {
    setEditedDate(newDate);
  };

  const handleDatePickerClose = () => {
    setIsDatePickerOpen(false);
  };

  const enterEditMode = () => {
    setIsEditMode(true);
    setIsConfirmDialogOpen(true);
  };

  return {
    isEditMode,
    editedDate,
    isConfirmDialogOpen,
    isDatePickerOpen,
    confirmSave,
    cancelEdit,
    handleCalendarClick,
    handleConfirmDialogClose,
    handleConfirmDialogSubmit,
    handleDatePickerChange,
    handleDatePickerClose,
    enterEditMode,
  };
};
